import { Paper, type SxProps } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { mergeSxProps } from "@src/appV2/redesign/system/ui-components";
import { type PaperVariant } from "@src/appV2/redesign/system/ui-theme";
import { type ForwardedRef, forwardRef, type ReactNode } from "react";

import { useEmbeddedMode } from "../../EmbeddedMode/EmbeddedModeContext";
import { useRedesignStatusBarAppearance } from "../utils/useRedesignStatusBarAppearance";

export interface PageWrapperProps {
  children: ReactNode;
  variant?: PaperVariant;
  sx?: SxProps<Theme>;
}

/**
 * This is minimalistic wrapper for a page. It has simple column flex layout as this is what we usually need
 * It's preferred to use components composition, so this component does not enforce any particular header, footer or margins
 */
function BasePageWrapper(props: PageWrapperProps, ref: ForwardedRef<HTMLDivElement>) {
  const { children, variant = "primary", sx } = props;
  const { isEmbedded } = useEmbeddedMode();

  // TODO: Remove this once we migrate to new shift discovery theme
  useRedesignStatusBarAppearance();

  return (
    <Paper
      ref={ref}
      variant={variant}
      sx={mergeSxProps(
        {
          // When embedded, use the CSS variable set by parent via postMessage
          // Falls back to 100vh if not set
          height: isEmbedded ? "var(--embedded-viewport-height, 100vh)" : "100vh",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        },
        sx
      )}
    >
      {children}
    </Paper>
  );
}

export const PageWrapper = forwardRef(BasePageWrapper);
